/**
 * Resolve an operation ID from a trading item object or direct string ID.
 * Used when handling DXChart visual selections, native clicks, and grouped selections.
 *
 * Priority order:
 * 1. model.data.operationId (explicit assignment in visual item)
 * 2. data.operationId (fallback from item data)
 * 3. model.originalItemId (parent operation for protection items)
 * 4. model.id (parent order/position visual item id)
 * 5. Direct string ID (from grouped selection or API callback)
 * 6. String ID with test-id prefix (position_item_*, order_item_*, protection_order_item_*)
 * 7. Fallback string ID (provided when visual item not found)
 */
export const resolveTradingItemOperationId = (
  itemOrId: any,
  fallbackItemId?: string,
): string | null => {
  // Try to extract operationId from item data (highest priority)
  if (itemOrId && typeof itemOrId === 'object') {
    const dataOperationId =
      typeof itemOrId?.model?.data?.operationId === 'string'
        ? itemOrId.model.data.operationId
        : typeof itemOrId?.data?.operationId === 'string'
          ? itemOrId.data.operationId
          : null

    if (dataOperationId && dataOperationId.length > 0) {
      return dataOperationId
    }

    // For protection items, prefer originalItemId (parent operation id)
    if (typeof itemOrId?.model?.originalItemId === 'string' && itemOrId.model.originalItemId.length > 0) {
      return itemOrId.model.originalItemId
    }

    // Fall back to model.id for parent order/position items
    if (typeof itemOrId?.model?.id === 'string' && itemOrId.model.id.length > 0) {
      return itemOrId.model.id
    }
  }

  // Direct string id (from grouped selection or native callback)
  if (typeof itemOrId === 'string' && itemOrId.length > 0) {
    // Strip test-id prefixes if present
    const prefixes = ['position_item_', 'order_item_', 'protection_order_item_']
    for (const prefix of prefixes) {
      if (itemOrId.startsWith(prefix)) {
        const stripped = itemOrId.substring(prefix.length)
        if (stripped.length > 0) {
          return stripped
        }
      }
    }

    // Return as-is if no prefix or after stripping
    return itemOrId
  }

  // Fallback from grouped item callback where item was not found
  if (typeof fallbackItemId === 'string' && fallbackItemId.length > 0) {
    return fallbackItemId
  }

  return null
}
